import { request, requestBlob } from './client';
import type {
  AcceptOrderResult,
  ActivateRechargeResult,
  ActiveSession,
  AdjustCreditResult,
  AdjustRechargeResult,
  AdminPricingPack,
  CreatedCustomer,
  CustomerCard,
  CustomerFilter,
  CustomerList,
  CustomerLoginResult,
  CreditHistory,
  CustomerTab,
  DeleteStationResult,
  Expense,
  ExpenseInput,
  ExpenseList,
  ExpiringBatch,
  ExtendPs5Result,
  FnbItem,
  FnbItemInput,
  FnbVendor,
  FnbVendorInput,
  HistorySession,
  LiveDashboard,
  QrChallenge,
  QrPollResult,
  PairingCode,
  PauseResumeResult,
  PayTabResult,
  PaymentMode,
  RefundMode,
  Pricing,
  PricingPackInput,
  PurchaseHistory,
  ReportOverview,
  RetireStationResult,
  RechargeAdjustmentList,
  Role,
  SellRechargeResult,
  SessionHistory,
  ShutdownResult,
  TvLockBackground,
  Staff,
  StartSessionResult,
  Station,
  StationLockResult,
  StationOrder,
  StationOrderStatus,
  StationTypeChangeResult,
  StationType,
  RemoveRechargeResult,
  PayStaffTabInput,
  RechargeRequest,
  RechargeRequestStatus,
  StaffTab,
  StaffTabSummary,
} from './types';

export const api = {
  auth: {
    // The counter signs in by QR only: a staff member already signed in on
    // the mobile app scans the code. Username and password live on the phone.
    qrStart: () => request<QrChallenge>('/auth/qr', { method: 'POST' }),
    qrPoll: (challengeId: string, pollSecret: string, signal?: AbortSignal) =>
      request<QrPollResult>(`/auth/qr/${challengeId}/poll`, {
        method: 'POST',
        body: { poll_secret: pollSecret },
        signal,
      }),
    me: () => request<Staff>('/auth/me'),
    // Change your own username and/or password. A wrong current password is a
    // 422, not a 401, so it surfaces in the form instead of signing you out.
    updateMe: (input: {
      current_password: string;
      username?: string;
      new_password?: string;
    }) => request<Staff>('/auth/me', { method: 'PATCH', body: input }),
    listStaff: () => request<Staff[]>('/auth/staff'),
    createStaff: (input: {
      name: string;
      username: string;
      password: string;
      role: Role;
    }) => request<Staff>('/auth/staff', { method: 'POST', body: input }),
    setStaffStatus: (staffId: string, isActive: boolean) =>
      request<Staff>(`/auth/staff/${staffId}/status`, {
        method: 'PATCH',
        body: { is_active: isActive },
      }),
  },

  customers: {
    list: (
      search: string,
      limit = 25,
      offset = 0,
      signal?: AbortSignal,
      filter: CustomerFilter = 'all',
    ) =>
      request<CustomerList>('/customers', {
        query: { search, filter, limit, offset },
        signal,
      }),
    get: (customerId: string, signal?: AbortSignal) =>
      request<CustomerCard>(`/customers/${customerId}`, { signal }),
    // Username and password are optional: left out, the server makes a
    // username from the name and sets the default password (1234).
    create: (input: {
      name: string;
      phone: string;
      notes?: string;
      username?: string;
      password?: string;
    }) => request<CreatedCustomer>('/customers', { method: 'POST', body: input }),
    update: (
      customerId: string,
      input: Partial<{
        name: string;
        phone: string;
        notes: string;
        is_active: boolean;
      }>,
    ) =>
      request<CustomerCard>(`/customers/${customerId}`, {
        method: 'PATCH',
        body: input,
      }),

    // Station sign-in: the username and password a customer types on a free
    // PC's lock screen to start their own session. Also how a forgotten
    // password is reset. `password` may be left out to change only the
    // username. Every customer has a sign-in; there is no way to remove one.
    setLogin: (customerId: string, input: { username: string; password?: string }) =>
      request<CustomerLoginResult>(`/customers/${customerId}/login`, {
        method: 'PUT',
        body: input,
      }),

    // Stored credit and every hand change to it. Adjusting is admin-only;
    // delta_amount is signed — negative takes credit away (handed back as cash).
    credit: (customerId: string, signal?: AbortSignal) =>
      request<CreditHistory>(`/customers/${customerId}/credit`, { signal }),
    adjustCredit: (customerId: string, input: { delta_amount: number; reason: string }) =>
      request<AdjustCreditResult>(`/customers/${customerId}/credit`, {
        method: 'POST',
        body: input,
      }),

    // The food & drink tab. Every write returns the whole updated tab, so the
    // card redraws from the response instead of refetching.
    tab: (customerId: string, signal?: AbortSignal) =>
      request<CustomerTab>(`/customers/${customerId}/tab`, { signal }),
    addToTab: (customerId: string, itemId: string, qty = 1) =>
      request<CustomerTab>(`/customers/${customerId}/tab`, {
        method: 'POST',
        body: { item_id: itemId, qty },
      }),
    // qty omitted removes the whole line. Only unpaid lines can be removed.
    removeFromTab: (customerId: string, tabItemId: string, qty?: number) =>
      request<CustomerTab>(`/customers/${customerId}/tab/${tabItemId}`, {
        method: 'DELETE',
        query: { qty },
      }),
    // Everything due, or only the named lines. The server totals them itself
    // at that moment — the screen's figure is never sent. amount_received is
    // what the customer handed over; more than needed is kept as credit.
    // partial: a part-payment by amount — it settles the oldest lines the
    // money covers and holds the rest as credit, leaving the tab open.
    // carry_balance: settle the lines even though amount_received is short;
    // the difference goes back on the tab as a balance line.
    payTab: (
      customerId: string,
      input: {
        payment_mode: PaymentMode;
        tab_item_ids?: string[];
        partial?: boolean;
        carry_balance?: boolean;
        amount_received?: number;
        cash_amount?: number;
        upi_amount?: number;
      },
    ) =>
      request<PayTabResult>(`/customers/${customerId}/tab/pay`, {
        method: 'POST',
        body: input,
      }),
    // Admin-only. Fixes how a recorded payment arrived; the amount and the
    // lines it settled never change. A split must add up to amount_received.
    changePaymentMode: (
      customerId: string,
      paymentId: string,
      input: { payment_mode: PaymentMode; cash_amount?: number; upi_amount?: number },
    ) =>
      request<{ tab: CustomerTab }>(
        `/customers/${customerId}/tab/payments/${paymentId}/mode`,
        { method: 'PATCH', body: input },
      ),
    // Everything the customer has paid for, newest first.
    purchases: (
      customerId: string,
      page: { limit?: number; offset?: number },
      signal?: AbortSignal,
    ) =>
      request<PurchaseHistory>(`/customers/${customerId}/tab/purchases`, {
        query: page,
        signal,
      }),
    // The settle-by date. null clears it.
    keepTabOpen: (customerId: string, until: string | null) =>
      request<CustomerTab>(`/customers/${customerId}/tab/keep-open`, {
        method: 'POST',
        body: { until },
      }),
  },

  fnb: {
    items: (signal?: AbortSignal) => request<FnbItem[]>('/fnb/items', { signal }),
    // Admin-only from here down; the backend returns 403 to staff.
    createItem: (input: FnbItemInput) =>
      request<FnbItem>('/fnb/items', { method: 'POST', body: input }),
    updateItem: (itemId: string, input: Partial<FnbItemInput>) =>
      request<FnbItem>(`/fnb/items/${itemId}`, { method: 'PATCH', body: input }),
    deleteItem: (itemId: string) =>
      request<Pick<FnbItem, 'item_id' | 'name'>>(`/fnb/items/${itemId}`, {
        method: 'DELETE',
      }),

    // The outside kitchens. Everyone reads; only an admin changes them. A
    // vendor with items cannot be deleted — switch it off instead.
    vendors: (signal?: AbortSignal) => request<FnbVendor[]>('/fnb/vendors', { signal }),
    createVendor: (input: FnbVendorInput) =>
      request<FnbVendor>('/fnb/vendors', { method: 'POST', body: input }),
    updateVendor: (vendorId: string, input: Partial<FnbVendorInput>) =>
      request<FnbVendor>(`/fnb/vendors/${vendorId}`, { method: 'PATCH', body: input }),
    deleteVendor: (vendorId: string) =>
      request<Pick<FnbVendor, 'vendor_id' | 'name'>>(`/fnb/vendors/${vendorId}`, {
        method: 'DELETE',
      }),

    // Orders placed from the stations' own screens. Polled from the app shell
    // so the counter is rung whichever page is open. Accept puts the lines on
    // the customer's tab; decline leaves it alone. Both are final — a second
    // click on the same order is a 409 naming who got there first.
    orders: (status: StationOrderStatus = 'pending', signal?: AbortSignal) =>
      request<StationOrder[]>('/fnb/orders', { query: { status }, signal }),
    acceptOrder: (orderId: string) =>
      request<AcceptOrderResult>(`/fnb/orders/${orderId}/accept`, { method: 'POST' }),
    declineOrder: (orderId: string) =>
      request<StationOrder>(`/fnb/orders/${orderId}/decline`, { method: 'POST' }),
  },

  recharges: {
    pricing: (signal?: AbortSignal) =>
      request<Pricing>('/recharges/pricing', { signal }),
    // Admin-only rate card editing. Changes affect the next sale; sold batches
    // keep their own minutes, price and expiry. A sold pack can only be hidden.
    packs: (signal?: AbortSignal) =>
      request<AdminPricingPack[]>('/recharges/packs', { signal }),
    createPack: (input: PricingPackInput) =>
      request<AdminPricingPack>('/recharges/packs', { method: 'POST', body: input }),
    updatePack: (packId: string, input: Partial<PricingPackInput>) =>
      request<AdminPricingPack>(`/recharges/packs/${packId}`, {
        method: 'PATCH',
        body: input,
      }),
    deletePack: (packId: string) =>
      request<Pick<AdminPricingPack, 'pricing_rule_id' | 'label'>>(
        `/recharges/packs/${packId}`,
        { method: 'DELETE' },
      ),
    sell: (input: {
      customer_id: string;
      pricing_rule_id: string;
      amount_paid?: number;
      minutes?: number;
      // Sold now, started later. A held pack is paid at the sale, so it
      // needs a payment mode; it never goes on the tab unpaid.
      on_hold?: boolean;
      payment_mode?: PaymentMode;
      /** Held packs only: false keeps the customer's stored credit untouched. */
      use_credit?: boolean;
      cash_amount?: number;
      upi_amount?: number;
      /** Answers a customer's app request; 409 if someone already did. */
      request_id?: string;
    }) => request<SellRechargeResult>('/recharges', { method: 'POST', body: input }),
    // Packs customers asked for from the HHG app. Polled from the app shell.
    requests: (status: RechargeRequestStatus = 'pending', signal?: AbortSignal) =>
      request<RechargeRequest[]>('/recharges/requests', { query: { status }, signal }),
    declineRequest: (requestId: string) =>
      request<RechargeRequest>(`/recharges/requests/${requestId}/decline`, { method: 'POST' }),
    // Admin-only. Starts a held pack; validity counts from activation_date
    // (YYYY-MM-DD, default today). Today, or an earlier day it really started.
    activate: (batchId: string, input: { activation_date?: string } = {}) =>
      request<ActivateRechargeResult>(`/recharges/${batchId}/activate`, {
        method: 'POST',
        body: input,
      }),
    expiring: (withinDays = 3, signal?: AbortSignal) =>
      request<ExpiringBatch[]>('/recharges/expiring', {
        query: { within_days: withinDays },
        signal,
      }),
    // Admin-only. delta_minutes is signed — negative takes time back — and the
    // reason is stored on the audit row, not just shown in a toast.
    adjust: (batchId: string, input: { delta_minutes: number; reason: string }) =>
      request<AdjustRechargeResult>(`/recharges/${batchId}/minutes`, {
        method: 'PATCH',
        body: input,
      }),
    // Admin-only. Ends a part-played pack: remaining minutes go to zero and
    // the customer pays charge_amount for it (0 waives it). An unpaid tab line
    // drops to that; a paid one refunds the difference — as credit, or cash/UPI
    // handed back — and reports take it off revenue on the day of removal.
    remove: (
      batchId: string,
      input: { charge_amount: number; reason: string; refund_mode?: RefundMode },
    ) =>
      request<RemoveRechargeResult>(`/recharges/${batchId}/remove`, {
        method: 'POST',
        body: input,
      }),
    adjustments: (
      filters: {
        customer_id?: string;
        adjusted_by?: string;
        limit?: number;
        offset?: number;
      } = {},
      signal?: AbortSignal,
    ) =>
      request<RechargeAdjustmentList>('/recharges/adjustments', {
        query: filters,
        signal,
      }),
  },

  stations: {
    // Retired stations are hidden unless asked for; only the admin setup
    // screen asks, so it can offer Restore.
    list: (signal?: AbortSignal, includeRetired = false) =>
      request<Station[]>('/stations', {
        query: includeRetired ? { include_retired: 'true' } : undefined,
        signal,
      }),
    create: (input: {
      name: string;
      station_type: StationType;
      display_order?: number;
      mac_address?: string;
    }) => request<Station>('/stations', { method: 'POST', body: input }),
    update: (
      stationId: string,
      input: Partial<{
        name: string;
        display_order: number;
        mac_address: string;
        status: 'idle' | 'maintenance';
      }>,
    ) => request<Station>(`/stations/${stationId}`, { method: 'PATCH', body: input }),
    pairingCode: (stationId: string) =>
      request<PairingCode>(`/stations/${stationId}/pairing-code`, { method: 'POST' }),
    unpair: (stationId: string) =>
      request<{ station_id: string; name: string }>(`/stations/${stationId}/unpair`, {
        method: 'POST',
      }),
    // Open a free seat with nobody on it. A number of minutes is a timer the
    // engine enforces; null leaves it open until blocked or a session starts.
    unblock: (stationId: string, minutes: number | null) =>
      request<StationLockResult>(`/stations/${stationId}/unblock`, {
        method: 'POST',
        body: { minutes },
      }),
    block: (stationId: string) =>
      request<StationLockResult>(`/stations/${stationId}/block`, { method: 'POST' }),
    // Admin only. The simulator rig sold as a standard PC, and back; refused
    // while a session is on the seat.
    changeType: (stationId: string, stationType: 'pc_standard' | 'pc_simulator') =>
      request<StationTypeChangeResult>(`/stations/${stationId}/type`, {
        method: 'POST',
        body: { station_type: stationType },
      }),
    // Admin only. Locks a free PC and asks its agent to power it off.
    shutdown: (stationId: string) =>
      request<ShutdownResult>(`/stations/${stationId}/shutdown`, { method: 'POST' }),
    // Admin only. The "delete": takes the seat off the floor but keeps its
    // history for Reports. Refused while a customer is on it.
    retire: (stationId: string) =>
      request<RetireStationResult>(`/stations/${stationId}/retire`, { method: 'POST' }),
    restore: (stationId: string) =>
      request<Station>(`/stations/${stationId}/restore`, { method: 'POST' }),
    // Admin only, retired stations only. Gone from every list for good.
    remove: (stationId: string) =>
      request<DeleteStationResult>(`/stations/${stationId}`, { method: 'DELETE' }),
  },

  // The picture behind every PS5 TV's lock screen. Writes are admin only.
  tvLockBackground: {
    get: (signal?: AbortSignal) =>
      request<TvLockBackground>('/tv-lock-background', { signal }),
    // The body is the image file itself, not JSON.
    upload: (file: Blob) =>
      request<{ sha256: string; content_type: string; bytes: number }>(
        '/tv-lock-background',
        { method: 'POST', file },
      ),
    // null goes back to the TVs' built-in background.
    use: (sha256: string | null) =>
      request<{ active_sha256: string | null }>('/tv-lock-background', {
        method: 'PUT',
        body: { sha256 },
      }),
    image: (sha256: string, signal?: AbortSignal) =>
      requestBlob(`/tv-lock-background/${sha256}/image`, signal),
  },

  sessions: {
    // A PS5 also needs the rate (player count × duration) the group is
    // paying for; its price goes on the customer's tab instead of a wallet.
    start: (stationId: string, customerId: string, ps5PricingId?: string) =>
      request<StartSessionResult>('/sessions/start', {
        method: 'POST',
        body: {
          station_id: stationId,
          customer_id: customerId,
          ...(ps5PricingId ? { ps5_pricing_id: ps5PricingId } : {}),
        },
      }),
    // PS5 only: the group buys more time. It stacks on what they have and
    // its price goes on the same tab.
    extend: (sessionId: string, ps5PricingId: string) =>
      request<ExtendPs5Result>(`/sessions/${sessionId}/extend`, {
        method: 'POST',
        body: { ps5_pricing_id: ps5PricingId },
      }),
    stop: (sessionId: string, reason: 'staff_stopped' | 'cancelled' = 'staff_stopped') =>
      request<HistorySession>(`/sessions/${sessionId}/stop`, {
        method: 'POST',
        body: { reason },
      }),
    // Stop the clock and lock the PC without ending the session; resume
    // unlocks and runs it again. The paused gap is never billed.
    pause: (sessionId: string) =>
      request<PauseResumeResult>(`/sessions/${sessionId}/pause`, { method: 'POST' }),
    resume: (sessionId: string) =>
      request<PauseResumeResult>(`/sessions/${sessionId}/resume`, { method: 'POST' }),
    // PS5 only: staff confirm the game has actually stopped after the booked
    // time ran out. Nothing else clears the alert except seating the next group.
    acknowledgeTimeUp: (sessionId: string) =>
      request<{ session_id: string; station_name: string }>(
        `/sessions/${sessionId}/acknowledge-time-up`,
        { method: 'POST' },
      ),
    active: (signal?: AbortSignal) =>
      request<ActiveSession[]>('/sessions/active', { signal }),
    history: (
      filters: {
        customer_id?: string;
        station_id?: string;
        from?: string;
        to?: string;
        limit?: number;
        offset?: number;
      },
      signal?: AbortSignal,
    ) => request<SessionHistory>('/sessions', { query: filters, signal }),
  },

  dashboard: {
    live: (signal?: AbortSignal) =>
      request<LiveDashboard>('/dashboard/live', { signal }),
  },

  // Money going out. Anyone can record one; correcting or deleting is admin.
  expenses: {
    list: (
      filters: { from?: string; to?: string; category?: string; limit?: number; offset?: number },
      signal?: AbortSignal,
    ) => request<ExpenseList>('/expenses', { query: filters, signal }),
    create: (input: ExpenseInput) =>
      request<Expense>('/expenses', { method: 'POST', body: input }),
    update: (expenseId: string, input: Partial<ExpenseInput>) =>
      request<Expense>(`/expenses/${expenseId}`, { method: 'PATCH', body: input }),
    remove: (expenseId: string) =>
      request<{ expense_id: string }>(`/expenses/${expenseId}`, { method: 'DELETE' }),
  },

  // Every staff member. What colleagues took on shift, at cost.
  staffTabs: {
    list: (signal?: AbortSignal) => request<StaffTabSummary[]>('/staff-tabs', { signal }),
    get: (staffId: string, signal?: AbortSignal) =>
      request<StaffTab>(`/staff-tabs/${staffId}`, { signal }),
    addItem: (staffId: string, itemId: string, qty = 1) =>
      request<StaffTab>(`/staff-tabs/${staffId}/items`, {
        method: 'POST',
        body: { item_id: itemId, qty },
      }),
    removeItem: (staffId: string, tabItemId: string, qty?: number) =>
      request<StaffTab>(`/staff-tabs/${staffId}/items/${tabItemId}`, {
        method: 'DELETE',
        query: { qty },
      }),
    pay: (staffId: string, input: PayStaffTabInput) =>
      request<StaffTab>(`/staff-tabs/${staffId}/pay`, { method: 'POST', body: input }),
  },

  // Admin only. Inclusive cafe dates, YYYY-MM-DD.
  reports: {
    overview: (range: { from: string; to: string }, signal?: AbortSignal) =>
      request<ReportOverview>('/reports/overview', { query: range, signal }),
  },
};
